import { createContext } from 'react';

import {
  ProfitType,
  SubTransportTypeOption,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { IntegrationCore } from 'types/Integration';
import { AvailableTabs } from 'types/QuoteView';
import { Maybe } from 'types/UtilityTypes';
import DefaultSidebarView from 'types/enums/DefaultSidebarView';
import { EmailProvider } from 'types/enums/EmailProvider';
import QuickQuoteReplyActionMode from 'types/enums/QuickQuoteReplyActionMode';

export type QuickQuoteConfig = {
  id: number;
  defaultPercentMargin: number;
  defaultFlatMargin: number;
  defaultTransportType: string;
  otherTransportTypes?: string[];
  defaultMarginType: string;
  lowConfidenceThreshold: number;
  mediumConfidenceThreshold: number;
  // If false, FE buttons will input quote into form but not submit it. User must submit manually
  isSubmitOnPortalEnabled: boolean;
  cubicFeetCalculationConfig?: CubicFeetCalculationConfig;
  datLaneHistoryLookbackMonths: number;
  datLaneHistoryAutomaticFetch: boolean;
  isFTLQuickQuoteDatesRequired: boolean;
  is3DVisualisationEnabled?: boolean;
  // When true, LTL side-loading and split/merge controls and optimization are enabled.
  isSideLoadingEnabled?: boolean;
  // When true, LTL ThreeG form hides equipment/carrier/services/compatibility constraints; defaults are used.
  hideConstraintSection?: boolean;
  // When true, Tai's limited liability section is hidden on LTL rate cards.
  hideTaiLimitedLiability?: boolean;
  // When true, Get LTL quote skips auto Shiplify for stops with an Address book externalTMSID.
  skipShiplifyForAddressBookStops?: boolean;
  // The service's usual LTL pickup hours as "HH:MM" (e.g. "08:00" and "16:00"). Tai LTL uses them
  // when the email gives no pickup time.
  defaultLtlPickupReadyTime?: string;
  defaultLtlPickupCloseTime?: string;
  // When true, FTL Quick Quote shows a USD/CAD currency toggle.
  isQuickQuoteCurrencyToggleEnabled?: boolean;
  // FTL Manual Quick Quote auto-selects this quote source as carrier cost when available.
  // Accepts a quote source (Quoting enum: greenscreens, dat, truckstop, sonar, kencoflash,
  // globaltranz, plus tms_lane_history) or a specific SelectedQuoteType (e.g. gs_network).
  // Empty preserves legacy behavior.
  defaultFTLBuyRateSource?: string;
  // Legacy flag: when true and quickQuoteReplyActionMode is unset, UI uses send_primary.
  isQuickQuoteSendReplyEnabled?: boolean;
  // draft_primary = draft buttons only (default). send_primary = Send on top, draft secondary.
  // send_only = Send only (e.g. customers that do not want draft reply).
  quickQuoteReplyActionMode?: QuickQuoteReplyActionMode;
  // Service-default recipients prefilled on Quick Quote draft response (editable before draft create).
  cc?: string[];
  bcc?: string[];
};

export type ServiceFeatureConfigurations = {
  defaultPriceMargin: Maybe<number>;
  defaultPriceMarginType: Maybe<ProfitType>;
};

export type Service = {
  serviceID: number;
  tmsIntegrations: IntegrationCore[];
  ltlTMSIntegrations: IntegrationCore[];
  quotingIntegrations: IntegrationCore[];
  schedulerIntegrations: IntegrationCore[];
  carrierVerificationIntegrations: IntegrationCore[];
  accessorialIntegrations: IntegrationCore[];
  configurations: ServiceFeatureConfigurations;
  serviceFeaturesEnabled: ServiceFeaturesListType;
  quickQuoteConfig?: QuickQuoteConfig;
  // Enum-derived granular equipment options (not config); returned top-level so they exist even
  // when a service has no quick_quote_configs row.
  subTransportTypes?: SubTransportTypeOption[];
  carrierQuoteConfig?: Maybe<CarrierQuoteConfig>;
  schedulerConfig?: SchedulerConfig;
  needsInboxReauthorization: boolean;
  emailProvider: Maybe<EmailProvider>;
  isSharedMailboxUser?: boolean;
};

export type CubicFeetCalculationConfig = {
  heightThresholdInches: number;
  overrideHeightInches: number;
  useLongestLinearLength: boolean;
};

export type ServiceFeaturesListType = {
  isLoadViewEnabled: boolean; // Load View
  isQuoteViewEnabled: boolean;

  // Load View Tabs (Load Info is on by default)
  isAppointmentSchedulingEnabled: boolean;
  isTrackAndTraceEnabled: boolean;
  isCarrierVerificationEnabled: boolean;

  // Quote View Tabs
  isQuickQuoteEnabled: boolean;
  isCarrierNetworkQuotingEnabled: boolean;
  isLoadBuildingEnabled: boolean;
  // Kit LB suggestion cards / fetch; independent of isLoadBuildingEnabled.
  // JSON name must match backend FeatureFlags.isLoadBuildingSuggestionCardsEnabled.
  isLoadBuildingSuggestionCardsEnabled: boolean;
  // Backend kill switch for load building extraction (FeatureFlags.disableLoadBuildingProcessing).
  // While it is on, nothing on the service can build a load from an email.
  disableLoadBuildingProcessing?: boolean;
  isTruckListEnabled: boolean;

  // Default quote view tab to open to
  defaultQuoteViewTab: AvailableTabs;
  // Default sidebar view when both Load (Active) and Quote (New) are enabled
  defaultSidebarView: DefaultSidebarView;

  // Load View - Load Info Tab sub-sections
  // Allows users to clone loads from email content using an existing load as the source
  isLoadCloningEnabled: boolean;
  // Allows users to copy a plaintext load summary from Load View Overview
  isLoadSummaryCopyEnabled: boolean;
  // Allows user to edit TMS object on Turvo instead of via Drumkit
  isTurvoSectionLinksEnabled: boolean;
  // Allows user to assign operator to load on Drumkit; TMS integration must implement this
  isOperatorEnabled: boolean;
  isOrderEnabled: boolean;
  // Track & Trace sub-sections / sub-features
  isCheckCallNotesEnabled: boolean;
  isExceptionsEnabled: boolean;
  isCheckCallCarrierSOPEnabled: boolean;
  isCheckCallShipperSOPEnabled: boolean;
  isCarrierEmailOutboxEnabled: boolean;

  // isCheckCallSuggestionsEnabled: boolean;
  isCarrierInfoSuggestionsEnabled: boolean;

  // Appointment Scheduling sub-sections / sub-features
  isAppointmentEmailingEnabled: boolean;
  isAppointmentSuggestionsEnabled: boolean;
  isAppointmentTMSUpdateEnabled: boolean; // Auto update TMS load with appointment details

  // Quick Quote sub-sections / sub-features
  // Allows handled quote requests to be reset and returned to pending before a rep requotes them.
  isQuoteRequestResetEnabled: boolean;
  // Allow users to submit quotes to third-party URLs, e.g. bidding websites
  isQuoteSubmissionViaURLEnabled: boolean;
  // Allows submitting quote to service's internal system (not the same as TMS)
  isQuoteSubmissionToServiceEnabled: boolean;
  isGetLaneRateFromServiceEnabled: boolean;
  // Allows access to quoting tools lane history (e.g. Greenscreens)
  isQuoteLaneHistoryEnabled: boolean;
  // Allows users to pull DAT Lane History
  isOnDemandDATLaneHistoryEnabled: boolean;
  // Allows users to update DAT RateView using custom parameters
  isUpdateRateViewEnabled: boolean;
  // Allows access to TMS lane history (e.g. McLeod)
  isTMSLaneHistoryEnabled: boolean;
  // Allow user to submit quote to TMS (TMS integration must implement CreateQuote)
  isTMSQuoteSubmissionEnabled: boolean;
  // Allow user to input profit margin rather than mark-up on QQ Calculator
  isQuoteCalculatorMarginEnabled: boolean;

  // Deprecated: advanced load search is enabled by default and is no longer a frontend gate.
  isAdvancedSearchEnabled: boolean;

  isMultiStopLoadViewEnabled: boolean;
  // Allow user to fetch Market Conditions from a DAT integration
  isDATMarketConditionsEnabled: boolean;
  // Allow user to fetch Market Conditions Forecast from a DAT integration
  isDATMarketConditionsForecastEnabled: boolean;
  // Allow user to fetch Load to Truck Ratio from a DAT integration
  isDATLoadToTruckRatioEnabled: boolean;

  isBatchQuoteEnabled: boolean;

  isMultiStopQuickQuoteEnabled: boolean;

  isLeanSolutionsCustomer: boolean;
  isPODCollectionEnabled: boolean;

  // Allows access to individual quick quote modes after legacy
  // isQuickQuoteEnabled has enabled the global Quick Quote UI.
  isFTLQuickQuoteEnabled: boolean;
  isLTLQuickQuoteEnabled: boolean;

  isQuickQuoteAgentEnabled: boolean;
  // Written-rules Quick Quote agents and the sidebar Kit Agent card.
  // The backend calls this V2; UI and code call it written rules.
  isQuickQuoteAgentV2Enabled: boolean;
  isQuickQuoteCoachEnabled: boolean;

  isCubicFeetCustomCalculationEnabled: boolean;
  // Allows users to submit bids to bidding portals from Quick Quote.
  isBiddingPortalSubmitBidEnabled: boolean;
  // Custom (non-TMS) Quick Quote Accessorials
  isQuickQuoteCustomAccessorialsEnabled: boolean;
  // Allows users to see fuel details in Quick Quote calculator
  isQuickQuoteFuelDetailsEnabled: boolean;
  // Granular equipment sub transport types in Quick Quote (grouped dropdown + analytics).
  isQuickQuoteSubTransportTypesEnabled: boolean;
  // City/state/postal autocomplete for regular Quick Quote location fields.
  isQQLocationAutocompleteEnabled: boolean;
};

export type CarrierQuoteConfig = {
  from?: Maybe<string>;
  cc?: Maybe<string[]>;
  bcc?: Maybe<string[]>;
  subject?: Maybe<string>;
  emailBody?: Maybe<string>;
  // bccCarriers is not currently used in FE, in API we currently only send emails with BCC carriers on
  // TODO: If we ever allow this to be off for a customer we can use this to conditionally render that behavior
  bccCarriers: boolean;
  isFindCarrierByGroupEnabled: boolean;
  isFindCarrierByLocationEnabled: boolean;
  // Customer Search input in Request Carriers section
  showCustomerSearch: boolean;
  // Item Description input in Request Carriers section
  showItemDescription: boolean;
  // Delivery Section in Request Carriers section
  showDeliverySection: boolean;
  // Require Delivery Location input in Request Carriers section
  requireDeliveryLocation: boolean;
  // Show Pickup Address Line 1 input in Request Carriers section
  showPickupAddressLine1: boolean;
  // Show Delivery Address Line 1 input in Request Carriers section
  showDeliveryAddressLine1: boolean;
  // Custom name for the carrier quote feature (used in header/tab title)
  customName: string;
};

export type SchedulerConfig = {
  scac: string;
};

export const allEnabledServiceFeatures: ServiceFeaturesListType = {
  isAppointmentSchedulingEnabled: true,
  isAppointmentEmailingEnabled: true,
  isTrackAndTraceEnabled: true,
  isQuickQuoteEnabled: true,
  isQuoteRequestResetEnabled: true,
  isAppointmentSuggestionsEnabled: true,
  isAppointmentTMSUpdateEnabled: true,
  isCarrierInfoSuggestionsEnabled: true,
  isLoadCloningEnabled: true,
  isLoadSummaryCopyEnabled: true,
  isTurvoSectionLinksEnabled: true,
  isOperatorEnabled: true,
  isOrderEnabled: true,
  isExceptionsEnabled: true,
  isCheckCallCarrierSOPEnabled: true,
  isCheckCallShipperSOPEnabled: true,
  isCheckCallNotesEnabled: true,
  isCarrierNetworkQuotingEnabled: true,
  isCarrierEmailOutboxEnabled: true,
  isAdvancedSearchEnabled: true,
  isCarrierVerificationEnabled: true,
  isTruckListEnabled: true,
  isTMSQuoteSubmissionEnabled: true,
  isLoadBuildingEnabled: true,
  isLoadBuildingSuggestionCardsEnabled: true,
  isQuoteLaneHistoryEnabled: true,
  isTMSLaneHistoryEnabled: true,
  isQuoteSubmissionViaURLEnabled: true,
  isQuoteSubmissionToServiceEnabled: true,
  isGetLaneRateFromServiceEnabled: true,
  isOnDemandDATLaneHistoryEnabled: true,
  isUpdateRateViewEnabled: true,
  isLoadViewEnabled: true,
  isQuoteViewEnabled: true,
  isQuoteCalculatorMarginEnabled: true,
  isMultiStopLoadViewEnabled: true,
  isDATMarketConditionsEnabled: true,
  isDATMarketConditionsForecastEnabled: true,
  isDATLoadToTruckRatioEnabled: true,
  isBatchQuoteEnabled: true,
  isMultiStopQuickQuoteEnabled: true,
  isLeanSolutionsCustomer: false,
  isPODCollectionEnabled: true,
  isLTLQuickQuoteEnabled: true,
  isFTLQuickQuoteEnabled: true,
  isQuickQuoteAgentEnabled: false,
  isQuickQuoteAgentV2Enabled: false,
  isQuickQuoteCoachEnabled: false,
  isCubicFeetCustomCalculationEnabled: true,
  isBiddingPortalSubmitBidEnabled: true,
  isQuickQuoteCustomAccessorialsEnabled: true,
  isQuickQuoteFuelDetailsEnabled: true,
  isQuickQuoteSubTransportTypesEnabled: false,
  isQQLocationAutocompleteEnabled: true,
  defaultQuoteViewTab: AvailableTabs.QuickQuote,
  defaultSidebarView: DefaultSidebarView.Unset,
};

export const initialServiceFeatureValues: ServiceFeaturesListType = {
  isAppointmentSchedulingEnabled: false,
  isAppointmentEmailingEnabled: false,
  isTrackAndTraceEnabled: false,
  isQuickQuoteEnabled: false,
  isQuoteRequestResetEnabled: false,
  isAppointmentSuggestionsEnabled: false,
  isAppointmentTMSUpdateEnabled: false,
  isCarrierInfoSuggestionsEnabled: false,
  isLoadCloningEnabled: false,
  isLoadSummaryCopyEnabled: false,
  isTurvoSectionLinksEnabled: false,
  isOperatorEnabled: false,
  isOrderEnabled: false,
  isExceptionsEnabled: false,
  isCheckCallCarrierSOPEnabled: false,
  isCheckCallShipperSOPEnabled: false,
  isCheckCallNotesEnabled: false,
  isCarrierNetworkQuotingEnabled: false,
  isCarrierEmailOutboxEnabled: false,
  isAdvancedSearchEnabled: true,
  isCarrierVerificationEnabled: false,
  isTruckListEnabled: false,
  isTMSQuoteSubmissionEnabled: false,
  isLoadBuildingEnabled: false,
  isLoadBuildingSuggestionCardsEnabled: false,
  isQuoteLaneHistoryEnabled: false,
  isTMSLaneHistoryEnabled: false,
  isQuoteSubmissionViaURLEnabled: false,
  isQuoteSubmissionToServiceEnabled: false,
  isGetLaneRateFromServiceEnabled: false,
  isOnDemandDATLaneHistoryEnabled: false,
  isUpdateRateViewEnabled: false,
  isLoadViewEnabled: false,
  isQuoteViewEnabled: false,
  isQuoteCalculatorMarginEnabled: false,
  isMultiStopLoadViewEnabled: false,
  isDATMarketConditionsEnabled: false,
  isDATMarketConditionsForecastEnabled: false,
  isDATLoadToTruckRatioEnabled: false,
  isBatchQuoteEnabled: false,
  isMultiStopQuickQuoteEnabled: false,
  isLeanSolutionsCustomer: false,
  isPODCollectionEnabled: false,
  isLTLQuickQuoteEnabled: false,
  isFTLQuickQuoteEnabled: false,
  isQuickQuoteAgentEnabled: false,
  isQuickQuoteAgentV2Enabled: false,
  isQuickQuoteCoachEnabled: false,
  isCubicFeetCustomCalculationEnabled: false,
  isBiddingPortalSubmitBidEnabled: false,
  isQuickQuoteCustomAccessorialsEnabled: false,
  isQuickQuoteFuelDetailsEnabled: true,
  isQuickQuoteSubTransportTypesEnabled: false,
  isQQLocationAutocompleteEnabled: false,
  defaultQuoteViewTab: AvailableTabs.QuickQuote,
  defaultSidebarView: DefaultSidebarView.Unset,
};

export const initialServiceValues: Service = {
  serviceID: 0,
  tmsIntegrations: [],
  ltlTMSIntegrations: [],
  quotingIntegrations: [],
  schedulerIntegrations: [],
  carrierVerificationIntegrations: [],
  accessorialIntegrations: [],
  configurations: { defaultPriceMargin: null, defaultPriceMarginType: null },
  serviceFeaturesEnabled: initialServiceFeatureValues,
  quickQuoteConfig: undefined,
  carrierQuoteConfig: null,
  needsInboxReauthorization: false,
  emailProvider: null,
  isSharedMailboxUser: false,
};

// The service flag key for written-rules agents. The backend calls this V2; UI and code call it
// written rules. Read the flag through isWrittenRulesAgentEnabled, not this key.
export const WRITTEN_RULES_AGENT_FLAG = 'isQuickQuoteAgentV2Enabled' as const;

export const isWrittenRulesAgentEnabled = (
  features: ServiceFeaturesListType
): boolean => Boolean(features[WRITTEN_RULES_AGENT_FLAG]);

export const isQuickQuoteUiEnabled = (
  features: ServiceFeaturesListType
): boolean => {
  return (
    features.isQuickQuoteEnabled &&
    (features.isFTLQuickQuoteEnabled || features.isLTLQuickQuoteEnabled)
  );
};

export type ServiceContextType = Service & {
  setService: React.Dispatch<React.SetStateAction<Service>>;
  updateConfigurations: (
    configurations: Partial<ServiceFeatureConfigurations>
  ) => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ServiceContext = createContext<ServiceContextType>({
  serviceID: 0,
  tmsIntegrations: [],
  ltlTMSIntegrations: [],
  quotingIntegrations: [],
  schedulerIntegrations: [],
  carrierVerificationIntegrations: [],
  accessorialIntegrations: [],
  configurations: { defaultPriceMargin: null, defaultPriceMarginType: null },
  serviceFeaturesEnabled: initialServiceFeatureValues,
  setService: () => false,
  updateConfigurations: () => {},
  isLoading: true,
  setIsLoading: () => false,
  needsInboxReauthorization: false,
  emailProvider: null,
  isSharedMailboxUser: false,
});
